import React, { useCallback, useRef, useState } from "react";
import WidgetCard from "../_shared/WidgetCard";
import ViewAllButton from "../_shared/ViewAllButton";
import ViewAllModal from "../modals/ViewAllModal";
import { StarIcon } from "../_shared/icons";
import { StudentDatum, VIEW_ALL_PAGE_SIZE } from "../data";
import { getTopStudents } from "shared/services/headOfficeService";
import { mapTopStudents } from "../apiMappers";
import type { ViewAllPage } from "../modals/ViewAllModal";
import styles from "./style.module.scss";

interface TopStudentsProps {
  students: StudentDatum[];
  /** `rank` is 1-based across pages — only rank 1 earns a certificate. */
  onOpenStudent: (student: StudentDatum, rank: number) => void;
  /** reporting window so the modal fetches the same range */
  win: { from: string; to: string };
  /** header + view-all copy overrides for campus-scoped reuse */
  title?: string;
  subtitle?: string;
  viewAllTitle?: string;
  viewAllSubtitle?: string;
  /** override the paginated fetch (campus reuse paginates static data) */
  fetchPage?: (page: number) => Promise<ViewAllPage>;
}

const StudentIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#EF437B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21a8 8 0 0 1 16 0" />
  </svg>
);

export default function TopStudents({
  students,
  onOpenStudent,
  win,
  title = "Top Students",
  subtitle = "Ranked by their reading and badge progress",
  viewAllTitle = "Top Students",
  viewAllSubtitle = "Network-wide — all students",
  fetchPage: fetchPageProp,
}: TopStudentsProps) {
  const [open, setOpen] = useState(false);
  const pageRef = useRef(1); // page currently rendered in the modal (for ranks)

  const renderRows = (
    list: StudentDatum[],
    offset = 0,
    clickable = false,
  ) =>
    list.map((s, i) => (
      <div
        key={`${s.name}-${offset + i}`}
        className={styles.row}
        role={clickable ? "button" : undefined}
        onClick={clickable ? () => onOpenStudent(s, offset + i + 1) : undefined}
      >
        <span className={styles.rank}>{offset + i + 1}</span>
        <div className={styles.info}>
          <div className={styles.name} data-ho-blur="student">
            {s.name}
          </div>
          <div className={styles.meta} data-ho-blur="campus">
            {s.meta}
          </div>
        </div>
        <span className={styles.badges}>
          <span className={styles.star}>
            <StarIcon />
          </span>
          <span className={styles.badgeCount}>{s.badges}</span>
        </span>
        <div className={styles.books}>
          <span className={styles.bookVal}>{s.books}</span>
          <span className={styles.bookUnit}> {s.books === 1 ? "book" : "books"}</span>
        </div>
      </div>
    ));

  const fetchPage = useCallback(
    async (page: number) => {
      pageRef.current = page;
      const res = await getTopStudents({ ...win, per_page: VIEW_ALL_PAGE_SIZE, page });
      const data = res?.data?.data ?? {};
      const p = data?.pagination ?? {};
      return {
        rows: mapTopStudents(data),
        total: Number(p.total) || 0,
        lastPage: Number(p.last_page) || 1,
        currentPage: Number(p.current_page) || page,
      };
    },
    [win.from, win.to],
  );

  const effectiveFetchPage = fetchPageProp ?? fetchPage;

  return (
    <WidgetCard
      title={title}
      subtitle={subtitle}
      icon={StudentIcon}
      right={<ViewAllButton onClick={() => setOpen(true)} />}
      flush
    >
      {renderRows(students.slice(0, 4), 0, true)}

      {open && (
        <ViewAllModal
          title={viewAllTitle}
          subtitle={viewAllSubtitle}
          onClose={() => setOpen(false)}
          fetchPage={effectiveFetchPage}
          renderContent={(rows) =>
            renderRows(rows as StudentDatum[], (pageRef.current - 1) * VIEW_ALL_PAGE_SIZE)
          }
        />
      )}
    </WidgetCard>
  );
}
